// Single source of truth for the Rechtsbijstand optional coverages.
//
// Previously the same list lived in four parallel copies that had to be kept in
// sync by hand: the RP payload builder (rpMapping), the merge-sum map (rpCodeMap),
// the final-save filter map (rpCodeToKey), and the JSX coverage grid. Adding or
// changing a coverage meant editing all four. They now derive from this one array.

export type RbvCoverageKind = 'section' | 'rp';

export interface RbvCoverage {
  /** Supportsection.Dekkingen key (the toggle key). */
  key: string;
  /** i18n key for the coverage row label. */
  labelKey: string;
  /** i18n coverageInfo key for the (i) popup. */
  infoKey: string;
  kind: RbvCoverageKind;
  /** Own PP block field (VK/IN/FV) — 'section' coverages only. */
  priceField: string | null;
  /** BFF RP_CODE in the PP.RP array — 'rp' coverages only. */
  code: string | null;
  /** Eigendom & Verhuur (9520) carries the rental-unit fields on its RP item. */
  rentalUnits?: boolean;
}

export const RBV_COVERAGES: RbvCoverage[] = [
  { key: 'Verkeer', labelKey: 'screens.rechtsbijstand.coverageOptions.verkeer', infoKey: 'verkeer', kind: 'section', priceField: 'VK', code: null },
  { key: 'Inkomen', labelKey: 'screens.rechtsbijstand.coverageOptions.inkomen', infoKey: 'inkomen', kind: 'section', priceField: 'IN', code: null },
  { key: 'Fiscaal_Vermogen', labelKey: 'screens.rechtsbijstand.coverageOptions.fiscaalVermogen', infoKey: 'fiscaalVermogen', kind: 'section', priceField: 'FV', code: null },
  { key: 'Eigendom_Verhuur_Onroerende_Zaken', labelKey: 'screens.rechtsbijstand.coverageOptions.eigendomVerhuur', infoKey: 'eigendomVerhuur', kind: 'rp', priceField: null, code: '9520', rentalUnits: true },
  { key: 'Statutair_directeur', labelKey: 'screens.rechtsbijstand.coverageOptions.statutairDirecteur', infoKey: 'statutairDirecteur', kind: 'rp', priceField: null, code: '9530' },
  { key: 'Bestuurder_Vereniging_van_Eigenaars', labelKey: 'screens.rechtsbijstand.coverageOptions.bestuurderVvE', infoKey: 'bestuurderVvE', kind: 'rp', priceField: null, code: '9531' },
  { key: 'Niet_consumentenkoop', labelKey: 'screens.rechtsbijstand.coverageOptions.nietConsumentenkoop', infoKey: 'nietConsumentenkoop', kind: 'rp', priceField: null, code: '9572' },
  { key: 'Financieel_onvermogen', labelKey: 'screens.rechtsbijstand.coverageOptions.financieelOnvermogen', infoKey: 'financieelOnvermogen', kind: 'rp', priceField: null, code: '9589' },
];

/** Coverages that ride the PP.RP array (have an RP_CODE). Order matches the payload. */
export const RBV_RP_COVERAGES = RBV_COVERAGES.filter((c) => c.kind === 'rp');

/** Coverages that get their own PP block (VK/IN/FV). */
export const RBV_SECTION_COVERAGES = RBV_COVERAGES.filter((c) => c.kind === 'section');

/** RP_CODE -> Dekkingen key. Replaces the old rpCodeMap / rpCodeToKey duplicates. */
export const RBV_RP_CODE_TO_KEY: Record<string, string> = Object.fromEntries(
  RBV_RP_COVERAGES.map((c) => [c.code as string, c.key]),
);
